import React, { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import CancelButton from "../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../components/ConfirmBoxModal";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import { toast } from "../../../../components/toastNotification";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { mainSelectedTab } from "../../atoms";
import { CarrierAgreementMgmtTabs } from "../../carrierAgreementTabs";
import {
  bLPAttachments,
  bLPAttachmentsForDelete,
  businessLicenseFromOffice,
  busLicensesAddState,
  busLicensesEditState,
  busLicensesSaving,
  disableBLOfficeField,
  rowForEditBusLicense,
  setLinkStateBusLicensePermit,
} from "../atoms/atoms";
import { AddBLPRequest, useAddBLP } from "../api/addBLP";
import { UpdateBLPRequest, useUpdateBLP } from "../api/updateBLP";
import { IBusLicensesAndPermitsDetails } from "../types";
import BusLicensesAndPermitsDetails from "./BusLicensesAndPermitsDetails";

interface BusLicensesAndPermitsFormProps {
  isAdd: boolean;
  isEdit: boolean;
  setIsAdd;
  setIsEdit;
}

interface NumericSelectValue {
  value?: string | number | null;
}

type NumericFormValue = string | number | NumericSelectValue | null | undefined;

type BLPFormValues = Omit<
  IBusLicensesAndPermitsDetails,
  "officeId" | "type" | "licenseStatus" | "renewalStatus"
> & {
  officeId?: NumericFormValue;
  type?: NumericFormValue;
  licenseStatus?: NumericFormValue;
  renewalStatus?: NumericFormValue;
};

const formatDateForInput = (
  value: string | Date | null | undefined,
): string => {
  if (!value) {
    return "";
  }

  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }

  return String(value).slice(0, 10);
};

const getNumericSourceValue = (
  value: NumericFormValue,
): string | number | null | undefined => {
  if (typeof value === "object" && value !== null) {
    return value.value;
  }

  return value;
};

const toNullableNumber = (value: NumericFormValue): number | null => {
  const sourceValue = getNumericSourceValue(value);

  if (sourceValue === null || sourceValue === undefined || sourceValue === "") {
    return null;
  }

  const numericValue = Number(sourceValue);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }

  return numericValue;
};

const toNullableTypeId = (value: NumericFormValue): number | null => {
  const numericValue = toNullableNumber(value);

  if (numericValue === null || !Number.isInteger(numericValue)) {
    return null;
  }

  return numericValue;
};

const hasUpdateChanges = (
  isEdit: boolean,
  isDirty: boolean,
  deletedAttachmentCount: number,
  addedAttachmentCount: number,
): boolean => {
  return (
    (isEdit && isDirty) ||
    deletedAttachmentCount > 0 ||
    addedAttachmentCount > 0
  );
};

const getIsActiveValue = (
  canSetIsActive: boolean,
  value: boolean | null | undefined,
): boolean | null | undefined => {
  if (canSetIsActive) {
    return value;
  }

  return true;
};

export const BusLicensesAndPermitsForm: React.FC<
  BusLicensesAndPermitsFormProps
> = ({ isAdd, isEdit, setIsAdd, setIsEdit }) => {
  const queryClient = useQueryClient();
  const history = useHistory();

  const [isLinkedState] = useRecoilState(setLinkStateBusLicensePermit);

  const [isAddState, setAddState] = useRecoilState(busLicensesAddState);

  const setEditState = useSetRecoilState(busLicensesEditState);

  const [isSaving, setIsSaving] = useRecoilState(busLicensesSaving);

  const [rowForEdit, setRowForEdit] = useRecoilState(rowForEditBusLicense);

  const attachments = useRecoilValue<(File | null)[]>(bLPAttachments);

  const [attachmentsForDelete, setAttachmentsForDelete] = useRecoilState(
    bLPAttachmentsForDelete,
  );

  const userPermissions = useRecoilValue(permissionsSelector);

  const deletePermission = PERMISSIONS.permission_buslicensesandpermits_delete;

  const [, setSelectedIndex] = useRecoilState(mainSelectedTab);

  const [viewBusinessFromOffice, setViewFromOffice] = useRecoilState(
    businessLicenseFromOffice,
  );

  const [, setDisableOfficeField] = useRecoilState(disableBLOfficeField);

  const [newData, setNewData] = useState<AddBLPRequest | UpdateBLPRequest>();

  const useFormMethods = useForm<BLPFormValues>({
    defaultValues: {
      ...(rowForEdit as BLPFormValues),
    },
  });

  const {
    reset,
    clearErrors,
    formState: { isDirty },
  } = useFormMethods;

  useEffect(() => {
    if (!rowForEdit) {
      return;
    }

    const editValues = rowForEdit as BLPFormValues;

    reset({
      ...editValues,

      officeId: toNullableNumber(editValues.officeId),

      issuedDate: formatDateForInput(editValues.issuedDate),

      renewedDate: formatDateForInput(editValues.renewedDate),

      expirationDate: formatDateForInput(editValues.expirationDate),

      createdDate: formatDateForInput(editValues.createdDate),

      type: toNullableTypeId(editValues.type),

      licenseStatus: toNullableNumber(editValues.licenseStatus),

      renewalStatus: toNullableNumber(editValues.renewalStatus),
    });
  }, [rowForEdit, reset]);

  const [saveModal, setSaveModal] = useState(false);

  const handleLink = () => {
    history.push({
      pathname: "/carrieragreementmgmt/busniessLicenseandpermit",
    });

    window.location.reload();
  };

  const handleLinkFromOffice = useCallback(
    (moduleName) => {
      const url = `/carrieragreementmgmt/${moduleName}`;

      history.push(moduleName);

      let userTabs = [
        {
          to: "",
        },
      ];

      [...CarrierAgreementMgmtTabs].forEach((tabs) => {
        const temp = [...userPermissions].filter(
          (user) => user === tabs.permission,
        );

        if (temp.length > 0) {
          userTabs.push({
            to: tabs.to,
          });
        }
      });

      userTabs.shift();

      const index = userTabs.findIndex((item) => item.to === url);

      setSelectedIndex(index);
    },
    [history, setSelectedIndex, userPermissions],
  );

  const handleCancel = useCallback(() => {
    if (viewBusinessFromOffice) {
      setViewFromOffice(false);

      handleLinkFromOffice("officesetting");
    }

    setDisableOfficeField(false);
    reset();
    clearErrors();
    setRowForEdit(undefined);
    setAddState(false);
    setEditState(false);
    setIsSaving(false);
    setIsAdd(false);
    setIsEdit(false);
  }, [
    clearErrors,
    handleLinkFromOffice,
    reset,
    setAddState,
    setDisableOfficeField,
    setEditState,
    setIsAdd,
    setIsEdit,
    setIsSaving,
    setRowForEdit,
    setViewFromOffice,
    viewBusinessFromOffice,
  ]);

  const onSubmit = (data: BLPFormValues) => {
    const officeId = toNullableNumber(data.officeId);

    const type = toNullableTypeId(data.type);

    const licenseStatus = toNullableNumber(data.licenseStatus);

    const renewalStatus = toNullableNumber(data.renewalStatus);

    const isActive = getIsActiveValue(
      userPermissions.includes(deletePermission) || isEdit,
      data.isActive,
    );

    const hasChanges = hasUpdateChanges(
      isEdit,
      isDirty,
      attachmentsForDelete.length,
      attachments.length,
    );

    const normalizedData = {
      ...data,

      officeId,
      type,
      licenseStatus,
      renewalStatus,
      attachments,
      isActive,
    };

    if (isAdd) {
      setNewData(normalizedData as AddBLPRequest);

      setSaveModal(true);
      return;
    }

    if (hasChanges) {
      setNewData({
        ...normalizedData,

        attachmentsForDelete:
          attachmentsForDelete as UpdateBLPRequest["attachmentsForDelete"],
      } as UpdateBLPRequest);

      setSaveModal(true);
      return;
    }

    setIsSaving(false);

    if (!isDirty) {
      toast.fire("Saving Failed", "No Changes have been made", "warning");
    }
  };

  // Add Business License & Permit Initialization
  const {
    mutate: addBusLicensePermit,
    isSuccess: addSuccess,
    isError: addError,
    data: addResult,
    reset: resetAddData,
  } = useAddBLP();

  // Edit Business License & Permit Initialization
  const {
    mutate: updateBusLicensePermit,
    isSuccess: editSuccess,
    isError: editError,
    data: editResult,
    reset: resetEditData,
  } = useUpdateBLP();

  const handleSave = () => {
    setSaveModal(false);

    if (!newData) {
      setIsSaving(false);

      toast.fire(
        "Saving Failed",
        "Business License and Permit data is missing.",
        "error",
      );

      return;
    }

    if (isAddState) {
      addBusLicensePermit(newData as AddBLPRequest);
    } else {
      updateBusLicensePermit(newData as UpdateBLPRequest);
    }

    if (isLinkedState) {
      setTimeout(() => handleLink(), 5000);
    }
  };

  useEffect(() => {
    if (addSuccess && addResult) {
      if (addResult.status === 201 || addResult.status === 200) {
        toast.fire(
          "Success",
          "Business License and Permit Added Successfully.",
          "success",
        );

        queryClient.invalidateQueries("GetBLPTable");

        queryClient.invalidateQueries("BLPDashboard");

        handleCancel();
        setAddState(false);
        setIsAdd(false);
      } else {
        toast.fire(
          "Server Error",
          "Failed to Add Business License and Permit! No Data Saved",
          "error",
        );

        setAddState(false);
      }

      resetAddData();
      setSaveModal(false);
      setIsSaving(false);
      return;
    }

    if (!addError) {
      return;
    }

    setIsSaving(false);
    setSaveModal(false);

    toast.fire(
      "Server Error",
      "Failed to Add Business License and Permit! No Data Saved",
      "error",
    );

    queryClient.invalidateQueries("GetBLPTable");

    setAddState(false);
    resetAddData();
  }, [
    addError,
    addResult,
    addSuccess,
    handleCancel,
    queryClient,
    resetAddData,
    setAddState,
    setIsAdd,
    setIsSaving,
  ]);

  useEffect(() => {
    if (editSuccess && editResult) {
      if (editResult.status === 201 || editResult.status === 200) {
        toast.fire(
          "Success",
          "Business License and Permit Updated Successfully.",
          "success",
        );

        queryClient.invalidateQueries("GetBLPTable");

        queryClient.invalidateQueries("BLPDashboard");

        handleCancel();
        setAttachmentsForDelete([]);
        setRowForEdit(undefined);
        setEditState(false);
        setIsEdit(false);
      } else {
        setIsSaving(false);

        toast.fire(
          "Server Error",
          "Failed to Update Business License and Permit! No Data Saved",
          "error",
        );

        queryClient.invalidateQueries("GetBLPTable");

        setEditState(false);
        setIsEdit(false);
      }

      resetEditData();
      return;
    }

    if (!editError) {
      return;
    }

    setIsSaving(false);
    setSaveModal(false);
    setIsEdit(false);

    toast.fire(
      "Server Error",
      "Failed to Update Business License and Permit! No Data Saved",
      "error",
    );

    queryClient.invalidateQueries("GetBLPTable");

    setEditState(false);
    setIsEdit(false);
    resetEditData();
  }, [
    editError,
    editResult,
    editSuccess,
    handleCancel,
    queryClient,
    resetEditData,
    setAttachmentsForDelete,
    setEditState,
    setIsEdit,
    setIsSaving,
    setRowForEdit,
  ]);

  return (
    <>
      <div className="p-3 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-2">
              <div className="font-bold text-primary text-base">
                {isAdd || isAddState
                  ? "Add Business License and Permit"
                  : "Edit Business License and Permit"}
              </div>

              <div className="flex justify-between gap-2">
                <SubmitHookForm
                  type="submit"
                  disabled={isSaving}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />

                <CancelButton
                  onClick={() => {
                    if (isLinkedState) {
                      handleLink();
                    }

                    handleCancel();
                  }}
                />
              </div>
            </div>

            <div className="border-b-2 border-secondary"></div>

            <div className="text-xs text-secondary italic font-normal mt-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>

            <div className="">
              <BusLicensesAndPermitsDetails rowForEdit={rowForEdit} />
            </div>
          </form>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={
            isAddState
              ? "Add Business License and Permit"
              : "Edit Business License and Permit"
          }
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this data?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);

            setIsSaving(false);
          }}
          onClickFunction={() => {
            handleSave();
            setIsSaving(true);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};
